import type {
  BooleanResponse,
  ConflictingUserAutomationPagedList,
  ConflictingUserAutomationPagedListResponse,
  Int32Response,
  PieceAutoAction,
  PieceAutoActionPagedList,
  PieceAutoActionPagedListResponse,
  PieceAutoActionRequest,
  PieceAutoActionResponse,
  PieceAutoActionsConflictsListParams,
  PieceAutoActionsConflictsResolveCreateParams,
  PieceAutoActionsListParams,
} from '@apiContract';

import { receivedDigitalApi } from './index';

const pieceAutoActionsApi = receivedDigitalApi.injectEndpoints({
  endpoints: (builder) => ({
    getPieceAutoActions: builder.query<PieceAutoActionPagedList, PieceAutoActionsListParams>({
      query: (params) => ({
        url: 'PieceAutoActions',
        params,
      }),
      providesTags: ['PieceAutoAction'],
      transformResponse: (response: PieceAutoActionPagedListResponse) => response.objectData,
    }),

    getPieceAutoActionsConflicts: builder.query<
      ConflictingUserAutomationPagedList,
      PieceAutoActionsConflictsListParams
    >({
      query: (params) => ({
        url: 'PieceAutoActions/conflicts',
        params,
      }),
      providesTags: ['PieceAutoActionConflicts'],
      transformResponse: (response: ConflictingUserAutomationPagedListResponse) => response.objectData,
    }),
    getPieceAutoActionsConflictsCount: builder.query<number, void>({
      query: () => ({
        url: 'PieceAutoActions/conflicts',
        params: {
          ItemsPerPage: 1,
          PageNumber: 0,
        },
      }),
      providesTags: ['PieceAutoActionConflicts'],
      transformResponse: (response: ConflictingUserAutomationPagedListResponse) => response.objectData.totalCount,
    }),

    deletePieceAutoActions: builder.mutation<boolean, number[]>({
      query: (requestData) => ({
        url: 'PieceAutoActions/list',
        method: 'DELETE',
        body: requestData,
      }),
      invalidatesTags: ['PieceAutoAction', 'PieceAutoActionConflicts'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),

    createPieceAutoAction: builder.mutation<PieceAutoAction, PieceAutoActionRequest>({
      query: (requestData) => ({
        url: 'PieceAutoActions',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['PieceAutoAction', 'PieceAutoActionConflicts'],
      transformResponse: (response: PieceAutoActionResponse) => response.objectData,
    }),

    updatePieceAutoAction: builder.mutation<PieceAutoAction, PieceAutoActionRequest>({
      query: (requestData) => ({
        url: 'PieceAutoActions',
        method: 'PUT',
        body: requestData,
      }),
      invalidatesTags: ['PieceAutoAction', 'PieceAutoActionConflicts'],
      transformResponse: (response: PieceAutoActionResponse) => response.objectData,
    }),

    deletePieceAutoAction: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `PieceAutoActions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PieceAutoAction', 'PieceAutoActionConflicts'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),
    resolveConflict: builder.mutation<boolean, PieceAutoActionsConflictsResolveCreateParams>({
      query: ({ userId, targetAutomationId }) => ({
        url: `PieceAutoActions/conflicts/resolve?userId=${userId}&targetAutomationId=${targetAutomationId}`,
        method: 'POST',
      }),
      invalidatesTags: ['PieceAutoAction', 'PieceAutoActionConflicts'],
      transformResponse: (response: BooleanResponse) => response.objectData,
    }),
    countConflictsByPiece: builder.mutation<number, number[]>({
      query: (body) => ({
        url: 'PieceAutoActions/conflicts/countByPiece',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Int32Response) => response.objectData,
    }),
  }),
});

export const {
  useGetPieceAutoActionsQuery,
  useGetPieceAutoActionsConflictsQuery,
  useDeletePieceAutoActionsMutation,
  useCreatePieceAutoActionMutation,
  useUpdatePieceAutoActionMutation,
  useDeletePieceAutoActionMutation,
  useResolveConflictMutation,
  useGetPieceAutoActionsConflictsCountQuery,
  useCountConflictsByPieceMutation,
} = pieceAutoActionsApi;
